import {
    setDefaults,
    fromAddress,
    OutputFormat,
    fromLatLng,
    geocode,
    RequestType,
} from "react-geocode";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { resizeAndCompressImage } from "./utils";

setDefaults({
    key: "AIzaSyCFmh_01jwtnD2wOacyn9C5RUCpa6nTdEg", // Your API key here.
    language: "en", // Default language for responses.
    region: "de", // Default region for responses.
    outputFormat: OutputFormat.JSON
});


const STORAGE_BASE_PATH = "/home/baila/baila_owner/";
const IMMUTABLE_CACHE_CONTROL = "public, max-age=31536000, immutable";

type EventMediaUploadResult = {
    eventImage: string;
    eventImageThumbUrl: string;
    eventImageCardUrl: string;
    eventImageHeroUrl: string;
    eventImageOriginalUrl: string;
    eventImageWidth: number;
    eventImageHeight: number;
};

type VariantSpec = {
    key: "original" | "thumb" | "card" | "hero";
    width: number;
    height: number;
    quality: number;
    preserveAspectRatio?: boolean;
    maxEdge?: number;
};

const EVENT_MEDIA_VARIANTS: VariantSpec[] = [
    { key: "original", width: 2000, height: 2000, quality: 0.92, preserveAspectRatio: true, maxEdge: 2000 },
    { key: "thumb", width: 360, height: 640, quality: 0.82 },
    { key: "card", width: 1200, height: 675, quality: 0.88 },
    { key: "hero", width: 1080, height: 1920, quality: 0.9 },
];

export async function getGeoCodeFromAdress(address: string) {
    let result = { lat: undefined, lng: undefined };
    try {
        const res = await fromAddress(address);
        if (res?.results) {
            const lat = res.results[0]?.geometry?.location?.lat;
            const lng = res.results[0]?.geometry?.location?.lng;
            result = { lat: lat, lng: lng };
        }
    }
    catch (err) {
        console.log(err)
    }
    return result;
}

export async function getAddressFromLatLng(lat: number, lng: number) {
    let res = null;
    // Get address from latitude & longitude.
    try {
        const resp = await geocode(RequestType.LATLNG, lat + "," + lng);
        const address = resp.results[0].formatted_address;
        res = address;
    } catch (error) {
        console.log(error)
    }
    return res;

}


export async function uploadToStorage(file: File, fileName: string): Promise<string | null> {
    if (file && fileName) {
        //compress image first
        const compressedFile = await resizeAndCompressImage(file);
        if (compressedFile) {
            const storage = getStorage();
            const storageRef = ref(storage, STORAGE_BASE_PATH + fileName);

            // Upload file to storage
            try {
                await uploadBytes(storageRef, compressedFile, {
                    contentType: "image/jpeg",
                    cacheControl: IMMUTABLE_CACHE_CONTROL,
                });

                // Get download URL for the uploaded file
                const downloadURL = await getDownloadURL(storageRef);
                return downloadURL;
            } catch (error) {
                console.error("Error uploading file:", error);
                return null;
            }
        } else {
            console.error("File couldnt be compressed.");
            return null;
        }
    } else {
        console.error("File or filename is missing.");
        return null;
    }
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error("Error loading image"));
        };
        image.src = objectUrl;
    });
}

function buildVariantBlob(
    image: HTMLImageElement,
    spec: VariantSpec,
): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            reject(new Error("Error creating canvas context"));
            return;
        }

        const sourceWidth = image.naturalWidth || image.width;
        const sourceHeight = image.naturalHeight || image.height;

        if (spec.preserveAspectRatio) {
            const longestEdge = Math.max(sourceWidth, sourceHeight);
            const scale = longestEdge > (spec.maxEdge ?? longestEdge)
                ? (spec.maxEdge ?? longestEdge) / longestEdge
                : 1;
            const width = Math.max(1, Math.round(sourceWidth * scale));
            const height = Math.max(1, Math.round(sourceHeight * scale));
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(image, 0, 0, width, height);
        } else {
            const scale = Math.max(spec.width / sourceWidth, spec.height / sourceHeight);
            const resizedWidth = Math.max(spec.width, Math.round(sourceWidth * scale));
            const resizedHeight = Math.max(spec.height, Math.round(sourceHeight * scale));
            const offsetX = Math.round((resizedWidth - spec.width) / 2);
            const offsetY = Math.round((resizedHeight - spec.height) / 2);

            canvas.width = spec.width;
            canvas.height = spec.height;
            ctx.drawImage(
                image,
                -offsetX,
                -offsetY,
                resizedWidth,
                resizedHeight,
            );
        }

        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error(`Error generating ${spec.key} image variant`));
                return;
            }
            resolve(blob);
        }, "image/jpeg", spec.quality);
    });
}

async function uploadBlobToStorage(blob: Blob, fileName: string): Promise<string> {
    const storage = getStorage();
    const storageRef = ref(storage, STORAGE_BASE_PATH + fileName);
    await uploadBytes(storageRef, blob, {
        contentType: "image/jpeg",
        cacheControl: IMMUTABLE_CACHE_CONTROL,
    });
    return getDownloadURL(storageRef);
}

function sanitizeEventMediaBaseName(fileName: string): string {
    const dotIndex = fileName.lastIndexOf(".");
    const baseName = dotIndex >= 0 ? fileName.slice(0, dotIndex) : fileName;
    return baseName.replace(/[^\w.-]+/g, "_");
}

export async function uploadEventMediaToStorage(
    file: File,
    fileName: string,
): Promise<EventMediaUploadResult | null> {
    if (!file || !fileName) {
        console.error("File or filename is missing.");
        return null;
    }

    try {
        const image = await loadImageFromFile(file);
        const baseName = sanitizeEventMediaBaseName(fileName);
        const uploads = await Promise.all(
            EVENT_MEDIA_VARIANTS.map(async (spec) => {
                const blob = await buildVariantBlob(image, spec);
                const downloadUrl = await uploadBlobToStorage(blob, `${baseName}_${spec.key}.jpg`);
                return [spec.key, downloadUrl] as const;
            }),
        );

        const urls = Object.fromEntries(uploads) as Record<VariantSpec["key"], string>;
        return {
            eventImage: urls.card,
            eventImageThumbUrl: urls.thumb,
            eventImageCardUrl: urls.card,
            eventImageHeroUrl: urls.hero,
            eventImageOriginalUrl: urls.original,
            eventImageWidth: image.naturalWidth || image.width,
            eventImageHeight: image.naturalHeight || image.height,
        };
    } catch (error) {
        console.error("Error uploading event media:", error);
        return null;
    }
}
